/**
 * useLive.js - how a screen reacts to other people's changes (liveEvents.js).
 *
 *   useLiveRefresh(kinds, refetch, { debounceMs, match })
 *       re-read your own data when a change of one of `kinds` arrives (and
 *       `match`, if given, agrees). A burst of changes is ONE refetch, and a
 *       resync (the connection came back after a drop) always refetches.
 *   useLiveEvent(match, handler)
 *       be told about each matching event as it arrives, e.g. to show "Paul
 *       just changed this record". A resync is offered as { kind: '*' }.
 *   matchRecord(base, id, subs) / matchKind(base, kind)
 *       ready-made matches that work inside and outside the GLOBAL KSA tab;
 *       recordPart(evt, base, id) says which part of a record an event's
 *       path names (the record itself, its purchases, its comments ...).
 *   latestReads()
 *       for a screen whose reads can overlap (its own load, a re-read after a
 *       save, a live re-read): an older answer landing late never wins.
 *
 * A background tab does not re-read at all: the refetch waits until the tab is
 * looked at again, then runs once. With a dozen tabs open around the office,
 * re-reading in every hidden one on every change would be most of the API's
 * traffic for screens nobody is watching. For the same reason a burst waits
 * 1 s after its last change, and a steady stream of saves (which never goes
 * quiet for 1 s) re-reads once every 3 s: every change makes each other open
 * tab re-read, and they all share the API's rate limit.
 */
import { useEffect, useRef } from 'react';
import { subscribe } from './liveEvents';

const tabHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

/**
 * Collects events into one run per burst. `run(keys)` gets the union of every
 * add(keys) since the last run. It waits `debounceMs` after the latest event,
 * but never more than `maxWaitMs` after the first (a steady trickle still
 * lands), and while the tab is hidden it waits for the tab to be shown.
 */
export function createBatcher(run, { debounceMs = 1000, maxWaitMs = Math.max(3000, debounceMs) } = {}) {
  let keys = new Set();
  let timer = null;
  let firstAt = 0;
  let waitingForView = false;
  let stopped = false;

  const fire = () => {
    timer = null;
    if (stopped) return;
    if (tabHidden()) {
      waitingForView = true;
      return;
    }
    const out = [...keys];
    keys = new Set();
    firstAt = 0;
    try {
      const p = run(out);
      if (p && typeof p.catch === 'function') p.catch((e) => console.warn('live refresh failed:', e && e.message));
    } catch (e) {
      console.warn('live refresh failed:', e && e.message);
    }
  };
  const onVisibility = () => {
    if (waitingForView && !tabHidden()) {
      waitingForView = false;
      fire();
    }
  };
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);

  return {
    add(list) {
      if (stopped) return;
      for (const k of list || []) keys.add(k);
      const now = Date.now();
      if (!firstAt) firstAt = now;
      clearTimeout(timer);
      timer = setTimeout(fire, Math.max(0, Math.min(debounceMs, firstAt + maxWaitMs - now)));
    },
    stop() {
      stopped = true;
      clearTimeout(timer);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}

const accepts = (match, evt) => {
  try {
    return !!match(evt);
  } catch {
    return false;
  }
};

/** Does a useLiveRefresh with these `kinds` / `match` want this event? */
export function wantsRefresh(kinds, match, evt) {
  if (!evt) return false;
  if (evt.resync) return true;
  const list = kinds == null ? [] : [].concat(kinds);
  if (list.length && !list.includes(evt.kind)) return false;
  return match ? accepts(match, evt) : true;
}

/**
 * Re-read when a matching change arrives. `kinds` is a kind or a list of them
 * ('projects', 'accounts' ...; null = any kind, leaving it to `match`).
 * `debounceMs` defaults to createBatcher's (1 s, at most 3 s for a stream).
 */
export function useLiveRefresh(kinds, refetch, { debounceMs, match } = {}) {
  const latest = useRef({ kinds, refetch, match });
  useEffect(() => {
    latest.current = { kinds, refetch, match };
  });
  useEffect(() => {
    const batch = createBatcher(() => latest.current.refetch(), { debounceMs });
    const off = subscribe((evt) => {
      const { kinds: k, match: m } = latest.current;
      if (wantsRefresh(k, m, evt)) batch.add();
    });
    return () => {
      off();
      batch.stop();
    };
  }, [debounceMs]);
}

/**
 * Reads of one thing that can overlap - a screen's own load, the re-read
 * after a save, a live re-read - must not land out of order: an older answer
 * arriving late would put old data back over newer. Each read takes a ticket
 * when it starts (`start()`); `show(ticket)` says whether its answer may be
 * shown - yes when no read started after it has been shown already, and it
 * then counts as shown. A read that fails shows nothing, so it never makes
 * another read count as old (a failed live re-read must not throw away this
 * tab's own re-read after a save). An answer applied straight from a save is
 * `show(start())`: the newest there is.
 */
export function latestReads() {
  let started = 0;
  let shown = 0;
  return {
    start: () => {
      started += 1;
      return started;
    },
    show: (ticket) => {
      if (!(ticket > shown)) return false;
      shown = ticket;
      return true;
    },
  };
}

/** Call `handler(evt)` for each event `match(evt)` accepts, as it arrives. */
export function useLiveEvent(match, handler) {
  const latest = useRef({ match, handler });
  useEffect(() => {
    latest.current = { match, handler };
  });
  useEffect(
    () =>
      subscribe((evt) => {
        const { match: m, handler: h } = latest.current;
        if (m && h && accepts(m, evt)) h(evt);
      }),
    []
  );
}

/**
 * The event kind a scoped tab's writes arrive under: useApiBase() is '/api'
 * normally (-> null) and the scope's own prefix inside one (the GLOBAL KSA tab:
 * '/api/<key>' -> '<key>', see context/DataScope.js).
 */
export function scopeKindOf(base) {
  const rest = String(base || '/api').replace(/^\/api\/?/, '');
  return rest ? rest.split('/')[0] : null;
}

/**
 * A match for the events of one record (a projects row, or a scoped tab's
 * record). `subs` names the parts of it the screen shows - e.g. ['comments'],
 * or null for the record's own fields.
 *   outside a scope: kind 'projects', the record's id, sub one of `subs`
 *   inside one:      kind = the scope, sub 'projects', the record's id. Such an
 *                    event does not say which part changed, so any matches.
 */
export function matchRecord(base, id, subs = null) {
  const scope = scopeKindOf(base);
  const want = id == null ? '' : String(id);
  return (evt) => {
    if (!evt || !want || evt.id == null || String(evt.id) !== want) return false;
    if (scope) return evt.kind === scope && evt.sub === 'projects';
    if (evt.kind !== 'projects') return false;
    return subs ? subs.includes(evt.sub) : !evt.sub;
  };
}

/** A match for every event of one resource kind ('attachments' ...), in or out of a scope. */
export function matchKind(base, kind) {
  const scope = scopeKindOf(base);
  return (evt) => !!evt && (scope ? evt.kind === scope && evt.sub === kind : evt.kind === kind);
}

const segmentsOf = (path) =>
  String(path)
    .split('?')[0]
    .split('/')
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });

/**
 * Which part of a record an event's `path` names: '' for the record itself
 * (<base>/projects/<id>), the segment under it for a part of it
 * ('purchases', 'comments', 'attachments' ...), or null when the event has
 * no path or another one. Inside a scope every event of a record arrives as
 * sub 'projects' (matchRecord), its purchases, notes and files too, so only
 * the path tells them from the record's own save.
 */
export function recordPart(evt, base, id) {
  if (!evt || typeof evt.path !== 'string' || !evt.path || id == null || id === '') return null;
  const want = [...segmentsOf(base || '/api'), 'projects', String(id)];
  const got = segmentsOf(evt.path);
  if (got.length < want.length || want.some((s, i) => got[i] !== s)) return null;
  return got.length === want.length ? '' : got[want.length];
}
